import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Modal } from '../../../components/ui/Modal';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  useCreditPolicies,
  useCreateCreditPolicy,
  useUpdateCreditPolicy,
  useDeleteCreditPolicy,
} from '../../../hooks/useCredit';
import type { CreditPolicy, CreateCreditPolicyInput, CreditProductType } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatPercent = (value: number) => {
  return `${(value * 100).toFixed(2)}%`;
};

const productTypeLabels: Record<CreditProductType, string> = {
  PERSONAL_LOAN: 'Empréstimo Pessoal',
  PAYROLL_LOAN: 'Crédito Consignado',
  RECEIVABLES_ADVANCE: 'Antecipação de Recebíveis',
  WORKING_CAPITAL: 'Capital de Giro',
};

const productTypeOptions = Object.entries(productTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

export function CreditPolicies() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<CreditPolicy | null>(null);
  const [formData, setFormData] = useState<Partial<CreateCreditPolicyInput>>({});

  const { data: policies, isLoading } = useCreditPolicies();
  const createPolicy = useCreateCreditPolicy();
  const updatePolicy = useUpdateCreditPolicy();
  const deletePolicy = useDeleteCreditPolicy();

  const handleOpenModal = (policy?: CreditPolicy) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        name: policy.name,
        description: policy.description,
        productType: policy.productType,
        minScore: policy.minScore,
        maxDebtToIncome: policy.maxDebtToIncome,
        minAccountAgeDays: policy.minAccountAgeDays,
        minMonthlyIncome: policy.minMonthlyIncome,
        minAmount: policy.minAmount,
        maxAmount: policy.maxAmount,
        minInstallments: policy.minInstallments,
        maxInstallments: policy.maxInstallments,
        monthlyInterestRate: policy.monthlyInterestRate,
        annualInterestRate: policy.annualInterestRate,
        autoApprovalThreshold: policy.autoApprovalThreshold,
        isActive: policy.isActive,
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        minScore: 400,
        maxDebtToIncome: 0.3,
        minAccountAgeDays: 30,
        minMonthlyIncome: 0,
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingPolicy) {
        await updatePolicy.mutateAsync({
          id: editingPolicy.id,
          data: formData,
        });
      } else {
        await createPolicy.mutateAsync(formData as CreateCreditPolicyInput);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar política:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Deseja desativar esta política?')) {
      await deletePolicy.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Políticas de Crédito</h1>
        <Button onClick={() => handleOpenModal()}>Nova Política</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {policies?.map((policy) => (
          <Card key={policy.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{policy.name}</CardTitle>
                  <Badge variant={policy.isActive ? 'success' : 'default'} className="mt-1">
                    {policy.isActive ? 'Ativa' : 'Inativa'}
                  </Badge>
                </div>
                <Badge variant="primary">
                  {productTypeLabels[policy.productType]}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Score Mínimo:</span>
                  <span>{policy.minScore}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Valor:</span>
                  <span>
                    {formatCurrency(policy.minAmount)} - {formatCurrency(policy.maxAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Parcelas:</span>
                  <span>
                    {policy.minInstallments} - {policy.maxInstallments}x
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Taxa Mensal:</span>
                  <span>{formatPercent(policy.monthlyInterestRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Taxa Anual:</span>
                  <span>{formatPercent(policy.annualInterestRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Auto-aprovação até:</span>
                  <span>{formatCurrency(policy.autoApprovalThreshold)}</span>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleOpenModal(policy)}
                >
                  Editar
                </Button>
                {policy.isActive && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(policy.id)}
                  >
                    Desativar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(!policies || policies.length === 0) && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            Nenhuma política cadastrada. Clique em "Nova Política" para criar.
          </CardContent>
        </Card>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPolicy ? 'Editar Política' : 'Nova Política'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <Select
            label="Tipo de Produto"
            value={formData.productType || ''}
            onChange={(e) =>
              setFormData({ ...formData, productType: e.target.value as CreditProductType })
            }
            options={productTypeOptions}
            required
            disabled={!!editingPolicy}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Score Mínimo"
              type="number"
              value={formData.minScore || ''}
              onChange={(e) =>
                setFormData({ ...formData, minScore: Number(e.target.value) })
              }
            />
            <Input
              label="Debt-to-Income Máx (%)"
              type="number"
              step="0.01"
              value={(formData.maxDebtToIncome || 0) * 100}
              onChange={(e) =>
                setFormData({ ...formData, maxDebtToIncome: Number(e.target.value) / 100 })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valor Mínimo (R$)"
              type="number"
              value={(formData.minAmount || 0) / 100}
              onChange={(e) =>
                setFormData({ ...formData, minAmount: Number(e.target.value) * 100 })
              }
              required
            />
            <Input
              label="Valor Máximo (R$)"
              type="number"
              value={(formData.maxAmount || 0) / 100}
              onChange={(e) =>
                setFormData({ ...formData, maxAmount: Number(e.target.value) * 100 })
              }
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Parcelas Mínimas"
              type="number"
              value={formData.minInstallments || ''}
              onChange={(e) =>
                setFormData({ ...formData, minInstallments: Number(e.target.value) })
              }
              required
            />
            <Input
              label="Parcelas Máximas"
              type="number"
              value={formData.maxInstallments || ''}
              onChange={(e) =>
                setFormData({ ...formData, maxInstallments: Number(e.target.value) })
              }
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Taxa Mensal (%)"
              type="number"
              step="0.01"
              value={(formData.monthlyInterestRate || 0) * 100}
              onChange={(e) =>
                setFormData({ ...formData, monthlyInterestRate: Number(e.target.value) / 100 })
              }
              required
            />
            <Input
              label="Taxa Anual (%)"
              type="number"
              step="0.01"
              value={(formData.annualInterestRate || 0) * 100}
              onChange={(e) =>
                setFormData({ ...formData, annualInterestRate: Number(e.target.value) / 100 })
              }
              required
            />
          </div>

          <Input
            label="Auto-aprovação até (R$)"
            type="number"
            value={(formData.autoApprovalThreshold || 0) / 100}
            onChange={(e) =>
              setFormData({ ...formData, autoApprovalThreshold: Number(e.target.value) * 100 })
            }
          />

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createPolicy.isPending || updatePolicy.isPending}
            >
              {createPolicy.isPending || updatePolicy.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CreditPolicies;
